import React, { FC, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  LinearProgress,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  TextField,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { makeStyles } from '@material-ui/core/styles';
import RocketLaunchIcon from '@material-ui/icons/FlightTakeoff';
import SpeedIcon from '@material-ui/icons/Speed';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import TimelineIcon from '@material-ui/icons/Timeline';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useTimeToMarketScore } from '../hooks/useTimeToMarketScore';
import { TimeToMarketMetricScore, METRIC_CATEGORIES } from '../api';

const useStyles = makeStyles(theme => ({
  mainCard: {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    position: 'relative' as const,
    overflow: 'hidden',
  },
  mainCardContent: {
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mainTitle: {
    fontSize: '1.8rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mainTitleIcon: {
    fontSize: '2rem',
  },
  scoreCircle: {
    width: 100,
    height: 100,
    borderRadius: '50%',
    border: `4px solid ${theme.palette.common.white}`,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  scoreValue: {
    fontSize: '2rem',
    fontWeight: 700,
  },
  scoreLabel: {
    fontSize: '0.7rem',
    opacity: 0.9,
  },
  metricCard: {
    borderRadius: 10,
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  metricCardContent: {
    padding: theme.spacing(2.5),
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  metricTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  metricIcon: {
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
  },
  categoryChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  metricDescription: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: 'auto',
  },
  progressBarElite: {
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#4caf50',
      borderRadius: 4,
    },
  },
  progressBarHigh: {
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#2196f3',
      borderRadius: 4,
    },
  },
  progressBarMedium: {
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#ff9800',
      borderRadius: 4,
    },
  },
  progressBarLow: {
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#f44336',
      borderRadius: 4,
    },
  },
  pointsText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    textAlign: 'right' as const,
  },
  infoCard: {
    marginTop: theme.spacing(3),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  infoHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(2),
  },
  infoHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoContent: {
    padding: theme.spacing(0, 2, 2, 2),
  },
  formulaBox: {
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(1.5, 0),
    textAlign: 'center' as const,
  },
  scoreTable: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      fontSize: '0.8rem',
    },
    '& .MuiTableCell-body': {
      fontSize: '0.8rem',
    },
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  dateRangeContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
  dateField: {
    minWidth: 150,
  },
  quickSelectContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: 'auto',
  },
  quickSelectLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
  quickSelectButton: {
    textTransform: 'none' as const,
    fontSize: '0.8rem',
    fontWeight: 500,
    minWidth: 'auto',
    padding: theme.spacing(0.25, 1),
  },
  quickSelectActive: {
    fontWeight: 700,
    textDecoration: 'underline',
  },
}));

const getCategoryStyle = (category: string): React.CSSProperties => {
  switch (category.toLowerCase()) {
    case METRIC_CATEGORIES.ELITE:
      return { backgroundColor: '#4caf50', color: '#fff' };
    case METRIC_CATEGORIES.HIGH:
      return { backgroundColor: '#2196f3', color: '#fff' };
    case METRIC_CATEGORIES.MEDIUM:
      return { backgroundColor: '#ff9800', color: '#fff' };
    default:
      return { backgroundColor: '#f44336', color: '#fff' };
  }
};

const getProgressClass = (
  category: string,
  classes: ReturnType<typeof useStyles>,
): string => {
  switch (category.toLowerCase()) {
    case METRIC_CATEGORIES.ELITE:
      return classes.progressBarElite;
    case METRIC_CATEGORIES.HIGH:
      return classes.progressBarHigh;
    case METRIC_CATEGORIES.MEDIUM:
      return classes.progressBarMedium;
    default:
      return classes.progressBarLow;
  }
};

interface MetricScoreCardProps {
  title: string;
  icon: React.ReactNode;
  score: TimeToMarketMetricScore;
  maxPoints: number;
}

const MetricScoreCard: FC<MetricScoreCardProps> = ({
  title,
  icon,
  score,
  maxPoints,
}) => {
  const classes = useStyles();
  const progressValue = (score.points / maxPoints) * 100;

  return (
    <Card className={classes.metricCard}>
      <CardContent className={classes.metricCardContent}>
        <Box className={classes.metricHeader}>
          <Typography className={classes.metricTitle}>
            {icon}
            {title}
          </Typography>
          <Chip
            label={score.category}
            size="small"
            className={classes.categoryChip}
            style={getCategoryStyle(score.category)}
          />
        </Box>
        <Typography className={classes.metricDescription}>
          {score.description}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          className={`${classes.progressBar} ${getProgressClass(
            score.category,
            classes,
          )}`}
        />
        <Typography className={classes.pointsText}>
          {score.points}/{maxPoints} points
        </Typography>
      </CardContent>
    </Card>
  );
};

interface SuccessRateCardProps {
  title: string;
  icon: React.ReactNode;
  successRate: string;
  points: number;
  maxPoints: number;
}

const SuccessRateCard: FC<SuccessRateCardProps> = ({
  title,
  icon,
  successRate,
  points,
  maxPoints,
}) => {
  const classes = useStyles();
  const progressValue = (points / maxPoints) * 100;
  const numericRate = parseInt(successRate, 10);
  let category = 'Low';
  if (numericRate >= 85) category = 'Elite';
  else if (numericRate >= 70) category = 'High';
  else if (numericRate >= 55) category = 'Medium';

  return (
    <Card className={classes.metricCard}>
      <CardContent className={classes.metricCardContent}>
        <Box className={classes.metricHeader}>
          <Typography className={classes.metricTitle}>
            {icon}
            {title}
          </Typography>
          <Chip
            label={`${successRate}`}
            size="small"
            className={classes.categoryChip}
            style={getCategoryStyle(category)}
          />
        </Box>
        <Typography className={classes.metricDescription}>
          Pipeline success rate
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          className={`${classes.progressBar} ${getProgressClass(
            category,
            classes,
          )}`}
        />
        <Typography className={classes.pointsText}>
          {points}/{maxPoints} points
        </Typography>
      </CardContent>
    </Card>
  );
};

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const startOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return {
    dateFrom: `${startOfPreviousMonth.getFullYear()}-${String(startOfPreviousMonth.getMonth() + 1).padStart(2, '0')}`,
    dateTo: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
  };
}

function formatDateRange(months: number): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), today.getMonth() - (months - 1), 1);
  return {
    dateFrom: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`,
    dateTo: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
  };
}

/** Convert "YYYY-MM" to full date string for API (start of month) */
function monthToStartDate(monthStr: string): string {
  const [year, month] = monthStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toISOString().split('T')[0];
}

/** Convert "YYYY-MM" to full date string for API (end of month) */
function monthToEndDate(monthStr: string): string {
  const [year, month] = monthStr.split('-').map(Number);
  return new Date(Date.UTC(year, month, 0)).toISOString().split('T')[0];
}

interface MetricScoreTableRow {
  label: string;
  definition?: string;
  points: number;
}

interface MetricScoreTableProps {
  rows: MetricScoreTableRow[];
  scoreColumnLabel?: string;
}

const MetricScoreTable: FC<MetricScoreTableProps> = ({
  rows,
  scoreColumnLabel = 'Score',
}) => {
  const classes = useStyles();
  const hasDefinition = rows.some(r => r.definition !== undefined);
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" className={classes.scoreTable}>
        <TableHead>
          <TableRow>
            <TableCell>{scoreColumnLabel}</TableCell>
            {hasDefinition && <TableCell>Definition</TableCell>}
            <TableCell align="right">Points</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.label}>
              <TableCell>{row.label}</TableCell>
              {hasDefinition && <TableCell>{row.definition}</TableCell>}
              <TableCell align="right">{row.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ScoreInfoSection: FC = () => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className={classes.infoCard} variant="outlined">
      <Box
        className={classes.infoHeader}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box className={classes.infoHeaderLeft}>
          <InfoOutlinedIcon color="action" />
          <Typography variant="subtitle2">
            How we calculate the Time to Market Score
          </Typography>
        </Box>
        <IconButton size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        <Box className={classes.infoContent}>
          <Typography variant="body2" paragraph>
            The <strong>Time to Market Score</strong> is a{' '}
            <strong>percentage from 0% to 100%</strong>. It combines four types
            of signals from your delivery toolchain. Each part can contribute up
            to its own maximum points; together they make a fixed total.
          </Typography>

          <Box className={classes.formulaBox}>
            <Typography variant="body2">
              <strong>
                Score = (points you earned &divide; maximum possible points)
                &times; 100%
              </strong>
            </Typography>
          </Box>

          <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
            A higher score means your observed{' '}
            <strong>deployment rhythm</strong>,{' '}
            <strong>change lead time</strong>,{' '}
            <strong>review/merge speed</strong>, and{' '}
            <strong>pipeline reliability</strong> are closer to the strongest
            bands we define.
          </Typography>

          {/* 1. Deployment Frequency */}
          <Typography className={classes.sectionTitle}>
            1. Deployment Frequency (max 6 points)
          </Typography>
          <Typography variant="body2" paragraph>
            How often changes go to production.
          </Typography>
          <MetricScoreTable
            rows={[
              { label: 'Elite', definition: 'Multiple deployments per day', points: 6 },
              { label: 'High', definition: 'Between once per day and once per week', points: 4 },
              { label: 'Medium', definition: 'Between once per week and once per month', points: 2 },
              { label: 'Low', definition: 'Fewer than once per month', points: 0 },
            ]}
          />

          {/* 2. Lead Time for Changes */}
          <Typography className={classes.sectionTitle}>
            2. Lead Time for Changes (max 6 points)
          </Typography>
          <Typography variant="body2" paragraph>
            How long it typically takes from work starting to production.
          </Typography>
          <MetricScoreTable
            rows={[
              { label: 'Elite', definition: 'Less than one day', points: 6 },
              { label: 'High', definition: 'Between one day and one week', points: 4 },
              { label: 'Medium', definition: 'Between one week and one month', points: 2 },
              { label: 'Low', definition: 'More than one month', points: 0 },
            ]}
          />

          {/* 3. Pull Request Time to Merge */}
          <Typography className={classes.sectionTitle}>
            3. Pull Request Time to Merge (max 3 points)
          </Typography>
          <Typography variant="body2" paragraph>
            How long pull requests stay open before merge.
          </Typography>
          <MetricScoreTable
            rows={[
              { label: 'Elite', definition: 'Less than one day', points: 3 },
              { label: 'High', definition: 'Between one day and one week', points: 2 },
              { label: 'Medium', definition: 'Between one week and one month', points: 1 },
              { label: 'Low', definition: 'More than one month', points: 0 },
            ]}
          />

          {/* 4. CI/CD Pipeline Success Rate */}
          <Typography className={classes.sectionTitle}>
            4. CI/CD Pipeline Success Rate (max 3 points)
          </Typography>
          <Typography variant="body2" paragraph>
            Share of pipeline runs that succeed.
          </Typography>
          <MetricScoreTable
            scoreColumnLabel="Success Rate"
            rows={[
              { label: '85% – 100%', points: 3 },
              { label: '70% – 85%', points: 2 },
              { label: '55% – 70%', points: 1 },
              { label: 'Under 55%', points: 0 },
            ]}
          />

          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              For more details, see the{' '}
              <Link
                href="https://bayergroup.sharepoint.com/sites/026557/SitePages/Common-Measures-Scores.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                Common Measures Scores
              </Link>{' '}
              documentation.
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

export const TimeToMarketScoreCard = (): JSX.Element => {
  const classes = useStyles();
  const { entity } = useEntity();

  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [activePreset, setActivePreset] = useState<number>(2);

  const handlePresetClick = useCallback((months: number) => {
    const range = formatDateRange(months);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setActivePreset(months);
  }, []);

  const handleDateFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
    setActivePreset(0);
  }, []);

  const handleDateToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
    setActivePreset(0);
  }, []);

  const params = useMemo(
    () => ({
      entityRef: `${entity.kind}:${entity.metadata.namespace || 'default'}/${
        entity.metadata.name
      }`,
      dateFrom: monthToStartDate(dateFrom),
      dateTo: monthToEndDate(dateTo),
    }),
    [entity.kind, entity.metadata.namespace, entity.metadata.name, dateFrom, dateTo],
  );

  const { data, loading, error } = useTimeToMarketScore(params);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (!data) {
    return (
      <Box p={2}>
        <Typography>No Time to Market Score data available.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Date Range Selection */}
      <Box className={classes.dateRangeContainer}>
        <TextField
          label="From"
          type="month"
          value={dateFrom}
          onChange={handleDateFromChange}
          className={classes.dateField}
          InputLabelProps={{ shrink: true }}
          inputProps={{ onKeyDown: (e: React.KeyboardEvent) => e.preventDefault() }}
          size="small"
          variant="outlined"
        />
        <TextField
          label="To"
          type="month"
          value={dateTo}
          onChange={handleDateToChange}
          className={classes.dateField}
          InputLabelProps={{ shrink: true }}
          inputProps={{ onKeyDown: (e: React.KeyboardEvent) => e.preventDefault() }}
          size="small"
          variant="outlined"
        />
        <Box className={classes.quickSelectContainer}>
          <Typography className={classes.quickSelectLabel}>Quick:</Typography>
          <Button
            color="primary"
            className={`${classes.quickSelectButton} ${activePreset === 2 ? classes.quickSelectActive : ''}`}
            onClick={() => handlePresetClick(2)}
          >
            Last 2 Months
          </Button>
          <Button
            color="primary"
            className={`${classes.quickSelectButton} ${activePreset === 3 ? classes.quickSelectActive : ''}`}
            onClick={() => handlePresetClick(3)}
          >
            Last 3 Months
          </Button>
          <Button
            color="primary"
            className={`${classes.quickSelectButton} ${activePreset === 6 ? classes.quickSelectActive : ''}`}
            onClick={() => handlePresetClick(6)}
          >
            Last 6 Months
          </Button>
          <Button
            color="primary"
            className={`${classes.quickSelectButton} ${activePreset === 12 ? classes.quickSelectActive : ''}`}
            onClick={() => handlePresetClick(12)}
          >
            Last 12 Months
          </Button>
        </Box>
      </Box>

      {/* Main DORA Metrics Card */}
      <Card className={classes.mainCard}>
        <CardContent className={classes.mainCardContent}>
          <Box>
            <Typography className={classes.mainTitle}>
              <TimelineIcon className={classes.mainTitleIcon} />
              DORA Metrics
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.85, marginTop: 4 }}>
              Software delivery performance indicators
            </Typography>
          </Box>
          <Box className={classes.scoreCircle}>
            <Typography className={classes.scoreValue}>
              {data.timeToMarketScorePercent}%
            </Typography>
            <Typography className={classes.scoreLabel}>
              Time to Market
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Individual Metric Cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricScoreCard
            title="Deployment Frequency"
            icon={<RocketLaunchIcon className={classes.metricIcon} />}
            score={data.deploymentFrequency}
            maxPoints={6}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricScoreCard
            title="Lead Time for Changes"
            icon={<SpeedIcon className={classes.metricIcon} />}
            score={data.leadTimeForChanges}
            maxPoints={6}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricScoreCard
            title="PR Time to Merge"
            icon={<MergeTypeIcon className={classes.metricIcon} />}
            score={data.meanTimeToMerge}
            maxPoints={3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SuccessRateCard
            title="CI/CD Pipeline Success"
            icon={<CheckCircleIcon className={classes.metricIcon} />}
            successRate={data.meanWorkflowRunsSuccessRate.successRate}
            points={data.meanWorkflowRunsSuccessRate.points}
            maxPoints={3}
          />
        </Grid>
      </Grid>

      {/* How Score is Calculated - Expandable Info Section */}
      <ScoreInfoSection />
    </Box>
  );
};
